/* eslint-disable react/jsx-curly-brace-presence */
import React, { useEffect, useState } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { LEARNING_ASSET_TYPES } from '../../Constants';
import VerbHeading from '../../components/VerbHeading';
import VerbButton, { VERB_BUTTON_TYPE } from '../../components/VerbButton';

const CollectionSubSection = ({ title, children, showDivider = false, isLockedReflection = false }) => (
    <div>
        <div
            style={{ ...(isLockedReflection ? { paddingBottom: 0 } : {}) }}
            className={showDivider ? 'show-divider' : ''}
        >
            {children}
        </div>
    </div>
);

CollectionSubSection.propTypes = {
    title: PropTypes.string,
    children: PropTypes.object,
    showDivider: PropTypes.bool,
    isLockedReflection: PropTypes.bool
};

const ReflectionPrompt = ({ text, subText, assetId, htmlText = null, hideNoButton = false }) => (
    <div className={'reflection-prompt'}>
        {htmlText || <VerbHeading hNumber={4} messageId={text} defaultMessage={text} />}
        {typeof subText === 'string' ? <p>{subText}</p> : subText}
        <div className={`col-12 ${hideNoButton? 'wide-button' : 'two-buttons'} prompt-buttons`}>
            {!hideNoButton && (
                <Link to={`/workspace/${assetId}?path=noRole`}>
                    <VerbButton className={'col-5 no-button'} buttonText={'NO'} buttonType={VERB_BUTTON_TYPE.SECONDARY_DEFAULT} />
                </Link>
            )}
            <Link to={`/workspace/${assetId}?path=yesRole`}>
                <VerbButton className={`col-5 yes-button`} buttonText={'YES'} buttonType={VERB_BUTTON_TYPE.PRIMARY_DEFAULT} heading6BtnText={true} />
            </Link>
        </div>
    </div>
);

ReflectionPrompt.propTypes = {
    text: PropTypes.string,
    subText: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
    assetId: PropTypes.string,
    htmlText: PropTypes.object,
    hideNoButton: PropTypes.bool
};

const RenderRow = ({
    filter,
    rowRenderer,
    asset,
    index,
    children,
    isLockedReflection = false,
    hideDivider = false,
    isReflection = false,
    status = ''
}) => {
    if (filter) {
        return (
            <>
                {rowRenderer(asset, index, isLockedReflection, hideDivider, isReflection, status)}
                {children}
            </>
        );
    }
    return null;
};

RenderRow.propTypes = {
    filter: PropTypes.bool,
    rowRenderer: PropTypes.func,
    asset: PropTypes.object,
    children: PropTypes.object,
    isLockedReflection: PropTypes.bool,
    index: PropTypes.number,
    hideDivider: PropTypes.bool,
    isReflection: PropTypes.bool,
    status: PropTypes.string
};

const MobileCollectionJourneySection = ({ learningAssets, isPracticalApplication = false, rowRenderer = () => {} }) => {
    const [takeActionCompleted, setTakeActionCompleted] = useState(true);
    const [takeActionCompletionDate, setTakeActionCompletionDate] = useState(null);
    const [reflectionActivityStarted, setReflectionActivityStarted] = useState(false);
    const [startSlide, setStartSlide] = useState(0);
    const [takeActionUserLearningAssetId, setTakeActionUserLearningAssetId] = useState('');

    useEffect(() => {
        learningAssets.forEach((asset) => {
            if (asset.type === LEARNING_ASSET_TYPES.PRACTICAL_APPLICATION) {
                if (asset.isCompleted || asset.status === 'COMPLETED') {
                    setTakeActionCompletionDate(asset.completedDateTime);
                    setTakeActionUserLearningAssetId(asset._id);
                    setStartSlide(asset.track?.sections[0]?.sectionItems.length - 2);
                } else {
                    setTakeActionCompleted(false);
                }
            }
            if (
                asset.type === LEARNING_ASSET_TYPES.REFLECTION &&
                (asset.isCompleted ||
                    asset.status === 'COMPLETED' ||
                    asset.status === 'IN_PROGRESS' ||
                    asset.completeness > 0)
            ) {
                setReflectionActivityStarted(true);
            }
        });
    }, [learningAssets]);

    const fourteenDays = moment(
        moment.utc(takeActionCompletionDate).startOf('day').add(14, 'days').format('LL')
    ).startOf('day');

    const fifteenDays = moment(
        moment.utc(takeActionCompletionDate).startOf('day').add(15, 'days').format('LL')
    ).startOf('day');

    const dueDate = fourteenDays.format('LL').split(',')[0];
    const today = new Date(Date.now());

    if (isPracticalApplication) {
        return (
            <div>
                <CollectionSubSection>
                    {learningAssets.map((asset, index) => (
                        <RenderRow
                            filter={
                                ![LEARNING_ASSET_TYPES.PRACTICAL_APPLICATION, LEARNING_ASSET_TYPES.REFLECTION].includes(
                                    asset.type
                                )
                            }
                            asset={asset}
                            index={index}
                            rowRenderer={rowRenderer}
                            status={asset.status}
                        />
                    ))}
                </CollectionSubSection>
                <CollectionSubSection>
                    {learningAssets.map((asset, index) => (
                        <RenderRow
                            filter={LEARNING_ASSET_TYPES.PRACTICAL_APPLICATION === asset.type}
                            asset={asset}
                            index={index}
                            rowRenderer={rowRenderer}
                            status={asset.status}
                        />
                    ))}
                </CollectionSubSection>
                <CollectionSubSection isLockedReflection={!takeActionCompleted}>
                    {learningAssets.map((asset, index) => (
                        <RenderRow
                            filter={LEARNING_ASSET_TYPES.REFLECTION === asset.type}
                            asset={asset}
                            index={index}
                            rowRenderer={rowRenderer}
                            isLockedReflection={!takeActionCompleted}
                            hideDivider
                            status={asset.status}
                            isReflection
                        >
                            {takeActionCompleted && !reflectionActivityStarted && today < fourteenDays.toDate() && (
                                <ReflectionPrompt
                                    htmlText={
                                        <p>
                                            You committed to take a{' '}
                                            <Link
                                                to={`/workspace/${takeActionUserLearningAssetId}?startSlide=${startSlide}`}
                                            >
                                                small action
                                            </Link>{' '}
                                            by {dueDate}
                                        </p>
                                    }
                                    subText={
                                        <>
                                            <p>
                                                Not done? Try your action this week.
                                            </p>
                                            <p className={'mt-2 reflection-question'} >Did you do your action?</p>
                                        </>
                                    }
                                    assetId={asset._id}
                                    hideNoButton
                                />
                            )}
                            {takeActionCompleted && !reflectionActivityStarted && (today > fifteenDays.toDate() || today > fourteenDays.toDate() &&
                             today < fifteenDays.toDate()) && (
                                <ReflectionPrompt
                                    htmlText={
                                        <p style={{textAlign: 'center'}}>
                                            You committed to take a{' '}
                                            <Link
                                                to={`/workspace/${takeActionUserLearningAssetId}?startSlide=${startSlide}`}
                                            >
                                                small action
                                            </Link>{' '}
                                            by {dueDate}.
                                        </p>
                                    }
                                    subText={
                                        <>
                                            <p style={{textAlign: 'center'}}>
                                                Whether you did it or not, continue this Path now.
                                            </p>
                                            <p style={{fontWeight: 600, marginTop: 16, marginBottom: 16, textAlign: 'center'}}>Did you do your action?</p>
                                        </>
                                    }
                                    assetId={asset._id}
                                />
                            )}
                        </RenderRow>
                    ))}
                </CollectionSubSection>
            </div>
        );
    }
    return learningAssets.map((asset, index) => rowRenderer(asset, index, !takeActionCompleted));
};

export default MobileCollectionJourneySection;
